import React, { useEffect, useState, useCallback } from 'react';
import { api, tokens, colaOffline, sincronizarCola } from './lib/api';
import { T, serif, sans, ROL_TEXTO } from './lib/ui';
import { Login, CambiarPassword } from './views/Acceso';
import { Panel, Capturar, MisGastos } from './views/Gastos';
import { Aprobar, Equipo, Encargos, Ovs, Usuarios } from './views/Gestion';
import { PanelSap } from './views/Sap';
import { Viaticos } from './views/Viaticos';
import { Reportes } from './views/Reportes';
import { Verificacion } from './views/Verificacion';
import { Revision } from './views/Revision';
import { Dtw } from './views/Dtw';
import { MiCuenta } from './views/MiCuenta';
import { InvitacionInstalar } from './views/Instalar';
import { Salvavidas } from './views/Salvavidas';
import { ImprimirLote } from './views/ImprimirLote';
import { Personal } from './views/Personal';

const MENU = [
  { id: 'panel',      texto: 'Panel',     roles: '*' },
  { id: 'mi-cuenta',  texto: 'Mi cuenta', roles: '*' },
  { id: 'capturar',   texto: 'Capturar',  roles: '*' },
  { id: 'mis-gastos', texto: 'Mis gastos',roles: '*' },
  { id: 'aprobar',    texto: 'Aprobar',   roles: ['mando_medio', 'director'] },
  { id: 'equipo',     texto: 'Mi equipo', roles: ['mando_medio', 'director'] },
  { id: 'viaticos',   texto: 'Viáticos',  roles: '*' },
  { id: 'imprimir',   texto: 'Imprimir',  roles: '*' },
  { id: 'encargos',   texto: 'Encargos',  roles: '*' },
  { id: 'ovs',        texto: 'OVs',       roles: '*' },
  // No depende del rol sino de un permiso concedido persona a persona.
  { id: 'personal',   texto: 'Personal',  roles: '*' },
  { id: 'reportes',   texto: 'Reportes',  permiso: 'veReportes' },
  { id: 'revision',   texto: 'Revisión',   roles: ['contabilidad', 'director'] },
  { id: 'verificacion', texto: 'Verificación', roles: ['contabilidad', 'director'] },
  { id: 'sap',        texto: 'SAP',       roles: ['contabilidad', 'director'] },
  { id: 'dtw',        texto: 'Enviar a SAP', roles: ['contabilidad', 'director'] },
  { id: 'usuarios',   texto: 'Usuarios',  roles: ['director'] },
];

export default function App() {
  const [usuario, setUsuario] = useState(tokens.usuario);
  const [vista, setVista] = useState('panel');
  const [enLinea, setEnLinea] = useState(navigator.onLine);
  const [enCola, setEnCola] = useState(colaOffline.leer().length);
  // Numero de serie que la plataforma acaba de asignar a un documento
  // interno. Se muestra una vez, en la pantalla a la que se navega
  // despues de registrar: si no, nadie se entera de que le toco.
  const [avisoDocumento, setAvisoDocumento] = useState(null);
  const [menuAbierto, setMenuAbierto] = useState(false);
  const [notaSync, setNotaSync] = useState('');

  // ── Estado de conexión ────────────────────────────────────────────
  useEffect(() => {
    const arriba = () => setEnLinea(true);
    const abajo  = () => setEnLinea(false);
    window.addEventListener('online', arriba);
    window.addEventListener('offline', abajo);
    return () => {
      window.removeEventListener('online', arriba);
      window.removeEventListener('offline', abajo);
    };
  }, []);

  // ── Sesión expirada ───────────────────────────────────────────────
  useEffect(() => {
    const salir = () => { tokens.limpiar(); setUsuario(null); };
    window.addEventListener('rinde:sesion-expirada', salir);
    return () => window.removeEventListener('rinde:sesion-expirada', salir);
  }, []);

  // ── Subir lo capturado sin señal ──────────────────────────────────
  useEffect(() => {
    if (!enLinea || !usuario || usuario.requiereCambioPassword) return;
    (async () => {
      const r = await sincronizarCola();
      setEnCola(r.pendientes);
      if (r.sincronizados > 0) {
        setNotaSync(`${r.sincronizados} gasto(s) capturado(s) sin señal ya se enviaron.`);
        setTimeout(() => setNotaSync(''), 6000);
      }
    })();
  }, [enLinea, usuario]);

  // ── Validar la sesión guardada al abrir la app ────────────────────
  useEffect(() => {
    if (!usuario) return;
    api.yo().then((r) => setUsuario(r.usuario)).catch(() => {});
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  const irA = useCallback((v) => { setVista(v); setMenuAbierto(false); }, []);

  const salir = async () => { await api.logout(); setUsuario(null); setVista('panel'); };

  if (!usuario) return <Login onEntrar={setUsuario} />;
  if (usuario.requiereCambioPassword) {
    return <CambiarPassword usuario={usuario} onListo={setUsuario} />;
  }

  const menu = MENU.filter((m) => {
    if (m.permiso) return Boolean(usuario[m.permiso]);
    return m.roles === '*' || m.roles.includes(usuario.rol);
  });

  const contenido = {
    panel:        <Panel usuario={usuario} irA={irA} />,

    'mi-cuenta':  <MiCuenta usuario={usuario} />,
    capturar:     <Capturar usuario={usuario} enLinea={enLinea} irA={irA}
                            onCapturado={(r) => {
                              setEnCola(colaOffline.leer().length);
                              if (r?.numeroAsignado || r?.avisoNumeracion) {
                                setAvisoDocumento({
                                  numero: r.numeroAsignado,
                                  problema: r.avisoNumeracion,
                                });
                              }
                            }} />,
    'mis-gastos': <MisGastos usuario={usuario} />,
    aprobar:      <Aprobar usuario={usuario} />,
    equipo:       <Equipo usuario={usuario} />,
    viaticos:     <Viaticos usuario={usuario} />,
    imprimir:     <ImprimirLote usuario={usuario} />,
    encargos:     <Encargos usuario={usuario} />,
    ovs:          <Ovs usuario={usuario} />,
    personal:     <Personal usuario={usuario} />,
    reportes:     usuario.veReportes ? <Reportes usuario={usuario} /> : null,
    revision:     <Revision usuario={usuario} />,
    dtw:          <Dtw usuario={usuario} />,
    verificacion: <Verificacion usuario={usuario} />,
    sap:          <PanelSap />,
    usuarios:     <Usuarios />,
  }[vista] || <Panel usuario={usuario} irA={irA} />;

  return (
    <div style={{ minHeight: '100vh', background: T.cream, fontFamily: sans, color: T.text }}>
      {/* Barra superior */}
      {/* La barra de estado del iPhone (hora, batería) se dibuja ENCIMA
          de la aplicación cuando está instalada. Sin compensar su
          altura, el menú y el nombre quedaban debajo y no se podían
          tocar. env(safe-area-inset-top) da esa altura, y vale 0 en un
          teléfono sin muesca y en el navegador. */}
      <header style={{
        position: 'sticky', top: 0, zIndex: 40, background: T.navy,
        // Doble red: iOS ya reserva la barra de estado con
        // status-bar-style "black", y además se suma el área segura por
        // si algún teléfono la reporta igual. Si vale 0, no estorba.
        paddingTop: 'calc(10px + env(safe-area-inset-top, 0px))',
        paddingBottom: 10,
        paddingLeft: 'calc(12px + env(safe-area-inset-left, 0px))',
        paddingRight: 'calc(12px + env(safe-area-inset-right, 0px))',
        // Alto mínimo para que la barra nunca quede aplastada contra
        // el borde superior.
        minHeight: 52,
        display: 'flex', alignItems: 'center',
        justifyContent: 'space-between', gap: 12,
      }}>
        {/* Área de toque de 44 px: es el mínimo que Apple recomienda
            para que un dedo acierte sin pelear con el borde. */}
        <button onClick={() => setMenuAbierto(!menuAbierto)}
                aria-label="Menú" style={{
          background: 'none', border: 'none', color: '#fff', fontSize: 24,
          cursor: 'pointer', lineHeight: 1, flexShrink: 0,
          minWidth: 48, minHeight: 48,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          // Sin margen negativo: pegarlo al borde es justo lo que hacía
          // que el dedo no lo alcanzara.
          borderRadius: 8,
        }}>
          {menuAbierto ? '\u00d7' : '\u2261'}
        </button>

        {/* Logo sobre placa blanca: el azul de la marca no contrasta
            lo suficiente contra el navy de la barra. */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 9,
          flexShrink: 0, minWidth: 0,
        }}>
          <span style={{
            background: '#fff', borderRadius: 6, padding: '5px 8px',
            flexShrink: 0,
            display: 'flex', alignItems: 'center',
          }}>
            <img src="/marca/logo-barra.png" alt="SICREA"
                 style={{ height: 20, display: 'block' }} />
          </span>
          <span style={{
            fontFamily: serif, fontSize: 18, fontWeight: 600,
            color: T.brass, fontStyle: 'italic',
          }}>
            Rinde
          </span>
        </div>

        {/* Bloque de la derecha: no crece ni empuja al logo. El nombre
            se recorta si hace falta, en lugar de desbordar la barra y
            arrinconar el menú. */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          minWidth: 0, flexShrink: 1, justifyContent: 'flex-end',
        }}>
          {!enLinea && (
            <span style={{
              fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 10,
              background: T.warnBg, color: T.warn, whiteSpace: 'nowrap',
            }}>
              SIN SEÑAL{enCola > 0 ? ` (${enCola})` : ''}
            </span>
          )}
          <span style={{
            overflow: 'hidden', textOverflow: 'ellipsis',
            whiteSpace: 'nowrap', minWidth: 0, color: T.brass, fontSize: 12 }}>
            {usuario.nombre.split(' ')[0]}
          </span>
        </div>
      </header>

      {/* Menú lateral */}
      {menuAbierto && (
        <>
          <div onClick={() => setMenuAbierto(false)} style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', zIndex: 45,
          }} />
          <nav style={{
            position: 'fixed', top: 0, left: 0, bottom: 0, width: 260, zIndex: 50,
            background: T.navy, overflowY: 'auto',
            // Arriba, la barra de estado; abajo, la barra de gestos del
            // iPhone: sin esto, la primera opción y "Cerrar sesión"
            // quedaban tapadas.
            paddingTop: 'calc(20px + env(safe-area-inset-top, 0px))',
            paddingBottom: 'calc(20px + env(safe-area-inset-bottom, 0px))',
            paddingLeft: 'env(safe-area-inset-left, 0px)',
          }}>
            <div style={{ padding: '0 20px 18px', borderBottom: `1px solid ${T.navyAlt}` }}>
              <div style={{ color: '#fff', fontWeight: 600, fontSize: 15 }}>{usuario.nombre}</div>
              <div style={{ color: T.brass, fontSize: 12, marginTop: 3 }}>
                {ROL_TEXTO[usuario.rol]} · {usuario.centro}
              </div>
            </div>

            {menu.map((m) => (
              <button key={m.id} onClick={() => irA(m.id)} style={{
                display: 'block', width: '100%', textAlign: 'left', padding: '13px 20px',
                background: vista === m.id ? T.navyAlt : 'transparent',
                borderLeft: `3px solid ${vista === m.id ? T.brass : 'transparent'}`,
                border: 'none', color: vista === m.id ? '#fff' : 'rgba(255,255,255,.75)',
                fontSize: 14, fontFamily: sans, cursor: 'pointer',
              }}>
                {m.texto}
              </button>
            ))}

            <div style={{ padding: '18px 20px 0', marginTop: 12, borderTop: `1px solid ${T.navyAlt}` }}>
              <button onClick={salir} style={{
                background: 'none', border: 'none', color: T.brass,
                fontSize: 13, cursor: 'pointer', padding: 0, fontFamily: sans,
              }}>
                Cerrar sesión
              </button>
            </div>
          </nav>
        </>
      )}

      {notaSync && (
        <div style={{
          background: T.okBg, color: T.ok, padding: '10px 16px',
          fontSize: 13, textAlign: 'center',
        }}>
          {notaSync}
        </div>
      )}

      <main style={{
        maxWidth: 860, margin: '0 auto',
        paddingTop: 22,
        paddingBottom: 'calc(60px + env(safe-area-inset-bottom, 0px))',
        paddingLeft: 'calc(16px + env(safe-area-inset-left, 0px))',
        paddingRight: 'calc(16px + env(safe-area-inset-right, 0px))',
      }}>
        {avisoDocumento && (
          <div
            onClick={() => setAvisoDocumento(null)}
            style={{
              background: avisoDocumento.problema ? T.warnBg : T.okBg,
              borderLeft: `3px solid ${avisoDocumento.problema ? T.warn : T.ok}`,
              borderRadius: '0 8px 8px 0', padding: '12px 16px',
              marginBottom: 16, cursor: 'pointer',
            }}>
            {avisoDocumento.numero && (
              <div style={{ fontSize: 13, color: T.ok }}>
                Documento registrado con el número{' '}
                <strong style={{ fontFamily: 'monospace', fontSize: 15 }}>
                  {avisoDocumento.numero}
                </strong>
              </div>
            )}
            {avisoDocumento.problema && (
              <div style={{ fontSize: 13, color: T.warn, lineHeight: 1.5 }}>
                {avisoDocumento.problema}
              </div>
            )}
          </div>
        )}
        <Salvavidas>{contenido}</Salvavidas>
      </main>

      <AvisoVersion />
      <InvitacionInstalar />
    </div>
  );
}


/**
 * Aviso de versión nueva.
 *
 * Antes, al desplegar, la app instalada podía quedarse con archivos de
 * dos versiones distintas y aparecía en blanco. Ahora se avisa y la
 * persona decide cuándo recargar, sin perder lo que esté escribiendo.
 */
function AvisoVersion() {
  const [hay, setHay] = useState(false);

  useEffect(() => {
    const alHaber = () => setHay(true);
    window.addEventListener('rinde:version-nueva', alHaber);
    return () => window.removeEventListener('rinde:version-nueva', alHaber);
  }, []);

  if (!hay) return null;

  return (
    <div style={{
      position: 'fixed', left: 12, right: 12, zIndex: 120,
      bottom: 'calc(12px + env(safe-area-inset-bottom, 0px))',
      background: T.navy, borderRadius: 10, padding: '13px 15px',
      display: 'flex', alignItems: 'center', gap: 12,
      boxShadow: '0 4px 18px rgba(0,0,0,.28)',
      maxWidth: 520, margin: '0 auto',
    }}>
      <span style={{ color: '#fff', fontSize: 13, flex: 1, lineHeight: 1.45 }}>
        Hay una versión nueva. Recarga cuando termines lo que estás haciendo.
      </span>
      <button
        onClick={() => {
          navigator.serviceWorker?.getRegistration()
            .then((r) => r?.waiting?.postMessage('actualizar'))
            .finally(() => window.location.reload());
        }}
        style={{
          background: T.brass, color: T.navy, border: 'none',
          padding: '8px 14px', borderRadius: 7, fontSize: 13,
          fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
        }}>
        Recargar
      </button>
    </div>
  );
}
